/**
 * reportPhasesStore.ts — M6 Output Factory · Increment 2 (2026-06-25, Anil).
 *
 * Persists staff-defined report PHASE definitions (carried from Inc-1 deferral #2,
 * owner-approved). Phases live at `clients/{cid}/reportPhases/{phaseId}` — team
 * read/create/update (firestore.rules Inc-2 additive matcher). So a named phase
 * survives reload before Inc 3 builds per-phase versioning on it.
 *
 * The pure phase shape + grouping is in reportPhases.ts; this is the impure seam.
 * Doc is project-scoped via a `projectId` field (a client can have several
 * projects, each with its own phases). The doc id is the stable phase id, but
 * de-duped per project by `${projectId}__${phaseId}` so two projects under one
 * client never collide.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ReportPhaseDef } from './reportPhases';

/** PURE — the project-scoped doc id for a phase. */
export function phaseDocId(projectId: string, phaseId: string): string {
  return `${projectId}__${phaseId}`;
}

function fromDoc(d: Record<string, unknown>): ReportPhaseDef {
  return {
    phaseId: (d.phaseId as string) || '',
    phaseNo: typeof d.phaseNo === 'number' ? d.phaseNo : 1,
    label: (d.label as string) || '',
    ...(typeof d.createdAt === 'string' ? { createdAt: d.createdAt } : {}),
  };
}

/** Stream a project's staff-defined report phases (team-read). */
export function listenReportPhaseDefs(
  clientId: string,
  projectId: string,
  cb: (defs: ReportPhaseDef[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  if (!clientId || !projectId) { cb([]); return () => {}; }
  const col = collection(db, 'clients', clientId, 'reportPhases');
  const q = query(col, where('projectId', '==', projectId));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((s) => fromDoc(s.data() as Record<string, unknown>)).sort((a, b) => a.phaseNo - b.phaseNo)),
    (e) => {
      console.error('[reportPhasesStore] listener error', e);
      onErr?.(e);
    },
  );
}

/** Create / update a staff-defined report phase. */
export async function saveReportPhaseDef(
  clientId: string,
  projectId: string,
  def: ReportPhaseDef,
  actorUid: string,
): Promise<void> {
  const id = phaseDocId(projectId, def.phaseId);
  await setDoc(
    doc(db, 'clients', clientId, 'reportPhases', id),
    {
      projectId,
      phaseId: def.phaseId,
      phaseNo: def.phaseNo,
      label: def.label,
      createdAt: def.createdAt || new Date().toISOString(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    },
    { merge: true },
  );
}
